import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectProduct, setVizLoading } from '../../../../store/visualizerSlice';
import Product from '../../Product/Product';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './ExtShutters.module.css';

// Surface to apply shutter selections
const SHUTTER_SURFACE = 'Shutters';

// Generate level label from style value (L1 -> Level 1, L6 -> Level 6)
const getLevelLabel = (style) => {
    if (!style) return style;
    const match = style.match(/^L(\d+)$/);
    if (match) {
        return `Level ${match[1]}`;
    }
    return style;
};

export default function ExtShutters({ 
    stepIndex,
    room = '',
    onViewAllApplied, 
    onNext, 
    onPrevious 
}) {
    const dispatch = useDispatch();
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'colors'
    const [profileData, setProfileData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProfileId, setSelectedProfileId] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [colorData, setColorData] = useState([]);
    const [colorLoading, setColorLoading] = useState(false);

    // Selection keys
    const profileSelectionKey = `ExtShutters_${room}_Profile`;
    const colorSelectionKey = `ExtShutters_${room}_Color`;


    // Restore selections from Redux on mount
    useEffect(() => {
        const savedProfile = stepSelections[profileSelectionKey];
        const savedColor = stepSelections[colorSelectionKey];
        if (savedProfile?.product) {
            setSelectedProfileId(savedProfile.product.id);
            setSelectedProfile(savedProfile.product);
        }
        if (savedColor?.product) {
            setSelectedColor(savedColor.product);
        }
    }, [stepSelections, profileSelectionKey, colorSelectionKey]);

    // Fetch profile products (series 9712)
    useEffect(() => {
        setLoading(true);
        setError(null);

        const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/9712.json`;
        
        fetch(URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch products: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setProfileData(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching products:', error);
                setError(error.message);
                setLoading(false);
            });
    }, []);

    // Fetch color products (series 9711)
    useEffect(() => {
        setColorLoading(true);

        fetch(`https://lizard.chameleonpower.com/CDN/ProductsBySeries/9711.json`)
            .then(response => response.json())
            .then(data => {
                setColorData(Array.isArray(data) ? data : []);
                setColorLoading(false);
            })
            .catch(err => {
                console.error('Error fetching colors:', err);
                setColorData([]);
                setColorLoading(false);
            });
    }, []);

    // Get unique levels from profile data and sort them
    const sortedLevels = useMemo(() => {
        const levels = [...new Set(profileData.map(p => p.style).filter(s => s && s !== ''))];
        return levels.sort((a, b) => {
            const numA = parseInt(a.replace('L', '')) || 0;
            const numB = parseInt(b.replace('L', '')) || 0;
            return numA - numB;
        });
    }, [profileData]);

    // Group profiles by level
    const groupedProfiles = useMemo(() => {
        const groups = {};
        
        // Products without style (null, undefined, or empty string) go to "Included" group
        const includedProducts = profileData.filter(p => !p.style || p.style === '');
        if (includedProducts.length > 0) {
            groups['Included'] = includedProducts;
        }
        
        sortedLevels.forEach(level => {
            const levelProducts = profileData.filter(p => p.style === level);
            if (levelProducts.length > 0) {
                groups[level] = levelProducts;
            }
        });
        
        return groups;
    }, [profileData, sortedLevels]);

    // Filter colors based on selected profile name
    const filteredColors = useMemo(() => {
        if (!selectedProfile?.name) {
            return [];
        }
        // Filter colors that match the profile name
        return colorData.filter(color => color.name === selectedProfile.name);
    }, [colorData, selectedProfile]);

    // Apply when both profile and color are selected
    useEffect(() => {
        if (selectedProfileId && selectedColor) {
            applyShutterSelections();
        }
    }, [selectedProfileId, selectedColor]);

    const handleProfileSelect = (product) => {
        setSelectedProfileId(product.id);
        setSelectedProfile(product);
        
        console.log('ExtShutters - Profile selected:', product.color || product.name);
        console.log('ExtShutters - Profile ID:', product.id);
        
        // Check if current color is valid for the new profile
        if (selectedColor && product.name) {
            const isColorValid = selectedColor.name === product.name;
            if (!isColorValid) {
                // Reset color and switch to colors tab
                setSelectedColor(null);
                setActiveTab('colors');
                console.log('ExtShutters - Color reset: not valid for new profile');
            }
        }
        
        // Store selection in Redux
        dispatch(selectProduct({
            product,
            surfaceName: 'ExtShutters_Profile',
            stepIndex,
            selectionKey: profileSelectionKey
        }));
    };

    const handleColorSelect = (product) => {
        setSelectedColor(product);
        
        console.log('ExtShutters - Color selected:', product.color || product.name);
        console.log('ExtShutters - Color ID:', product.id);
        
        // Store selection in Redux
        dispatch(selectProduct({
            product,
            surfaceName: 'ExtShutters_Color',
            stepIndex,
            selectionKey: colorSelectionKey
        }));
    };

    const applyShutterSelections = () => {
        const chamViz = document.getElementById('chamViz');
        if (!chamViz) {
            console.error('ExtShutters - chamViz element not found');
            return;
        }

        if (!selectedProfileId || !selectedColor?.id) {
            console.error('ExtShutters - Missing profile or color selection');
            return;
        }

        const colorId = selectedColor.id;
        
        // Build sel string: profileId(colorId)
        const shutterSel = `${selectedProfileId}(${colorId})`;
        
        console.log('ExtShutters - Applying selection:', shutterSel, '-> Shutters');
        
        dispatch(setVizLoading(true));
        
        chamViz.applyByFilter(
            shutterSel,
            "",
            function(newSel) {
                if (this.name === SHUTTER_SURFACE) {
                    return shutterSel;
                }
                return this.sel;
            },
            true
        );
    };

    const handleViewAllApplied = () => {
        if (onViewAllApplied) {
            onViewAllApplied();
        }
    };


    return (
        <div className={styles.container}>
            <StepNavigation
                title="Shutters"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={handleViewAllApplied}
            />

            {/* Tab Navigation */}
            <div className={styles.tabContainer}>
                <button
                    className={`${styles.tab} ${activeTab === 'profile' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    PROFILE
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'colors' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('colors')}
                >
                    COLORS
                </button>
            </div>

            {/* Selection Status */}
            <div className={styles.selectionStatus}>
                <span className={selectedProfileId ? styles.selected : ''}>
                    Profile: {selectedProfile ? (selectedProfile.color || selectedProfile.name || 'Selected') : 'Not selected'}
                </span>
                <span className={selectedColor ? styles.selected : ''}>
                    Color: {selectedColor ? (selectedColor.color || selectedColor.name) : 'Not selected'}
                </span>
            </div>

            {/* Product List - Profile Tab */}
            {activeTab === 'profile' && (
                <div className={`${styles.productListContainer} ${styles.shuttersProducts}`} data-series="9712">
                    {loading && (
                        <div className={styles.message}>Loading products...</div>
                    )}

                    {error && (
                        <div className={styles.errorMessage}>Error: {error}</div>
                    )}

                    {!loading && !error && Object.keys(groupedProfiles).length === 0 && (
                        <div className={styles.message}>No products available.</div>
                    )}

                    {!loading && !error && Object.entries(groupedProfiles).map(([level, products]) => (
                        <div key={level} className={styles.tierSection}>
                            <div className={styles.tierHeader}>
                                <span className={styles.tierTitle}>
                                    {level === 'Included' ? 'Included' : getLevelLabel(level)}
                                </span>
                                <div className={styles.tierLine}></div>
                            </div>
                            <ul className={styles.productGrid}>
                                {products.map(product => (
                                    <Product
                                        key={product.id}
                                        id={product.id}
                                        specifier={product.specifier}
                                        focus={500}
                                        size="256"
                                        title={product.color || ''}
                                        description={product.name || ''}
                                        series="9712"
                                        onSelect={(id) => {
                                            const selected = products.find(p => p.id === id);
                                            if (selected) handleProfileSelect(selected);
                                        }}
                                        isActive={product.id === selectedProfileId}
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {/* Product List - Colors Tab */}
            {activeTab === 'colors' && (
                <div className={`${styles.productListContainer} ${styles.shuttersProducts}`} data-series="9711">
                    {!selectedProfile && (
                        <div className={styles.message}>Please select a shutter profile first.</div>
                    )}

                    {selectedProfile && colorLoading && (
                        <div className={styles.message}>Loading colors...</div>
                    )}

                    {selectedProfile && !colorLoading && filteredColors.length === 0 && (
                        <div className={styles.message}>No colors available for this profile.</div>
                    )}

                    {selectedProfile && !colorLoading && filteredColors.length > 0 && (
                        <ul className={styles.productGrid}>
                            {filteredColors.map(product => (
                                <Product
                                    key={product.id}
                                    id={product.id}
                                    specifier={product.specifier}
                                    focus={500}
                                    size="256"
                                    title={product.color || ''}
                                    description=""
                                    series="9711"
                                    onSelect={(id) => {
                                        const selected = filteredColors.find(p => p.id === id);
                                        if (selected) handleColorSelect(selected);
                                    }}
                                    isActive={product.id === selectedColor?.id}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

ExtShutters.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
